import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { connect } from 'react-redux';
import { Switch } from 'react-router';
import { BrowserRouter, Route } from 'react-router-dom';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */

/* Theme variables */
import './App.scss';
import { RootState } from './models/RootState';
// import './theme/variables.css';

import { HelmetProvider } from 'react-helmet-async';
import { setTimeout } from 'timers';
import { BRAND_DOMAIN, BRAND_NAME, domain } from './constants/Branding';
import defaultLangData from './locales/default-lang.json';
import { AlertDTO } from './models/Alert';
import LoadingPage from './pages/LoadingPage/LoadingPage';
import { fetchMailVerifiedSuccess, logout, setLangData } from './store';
import {
  setAlertMsg,
  setDomainConfig,
  setLangSelected,
  setLanguages,
} from './store/common/commonActions';
import SVLS_API from './svls-api';
import {
  defaultLang,
  getLang,
  getLangCode,
  getSelectedLang,
  getUpdatedSelectedLang,
} from './util/localizationUtil';
import LANG_API from './api-services/language-api';
import { DomainConfig } from './models/DomainConfig';
import { getEnvVariable } from './constants/whitelabelEnv';
import Maintenance from './pages/Maintenance/Maintenance';

const SignUp = lazy(() => import('./pages/SignUp/SignUp'));
const LoginPage = lazy(() => import('./pages/Login/LoginPage'));
const ResetPassword = lazy(
  () => import('./pages/ResetPasswordPage/ResetPasswordPage')
);
const AccessRedirect = lazy(
  () => import('./pages/AccessRedirect/Accessredirect')
);
const AcceptTerms = lazy(() => import('./pages/AcceptTerms/AcceptTerms'));
const ForgotPwdForm = lazy(
  () => import('./components/ForgotPassword/ForgotPassword')
);
const MainPage = lazy(() => import('./router/UserRouter'));

type StateProps = {
  fetchMailVerifiedSuccess: (mailVerified: boolean) => void;
  logout: () => void;
  mailVerified: boolean;
  prefersDark: string;
  loggedIn: boolean;
  setLanguages: (languages: string[]) => void;
  setLangSelected: (lang: string) => void;
  setLangData: (langData: any) => void;
  langData: string;
  setAlertMsg: Function;
  setDomainConfig: (config: DomainConfig) => void;
};

const loadTheme = () => {
  const cssFolderName = getEnvVariable(domain, 'FOLDER_NAME') || 'default.css';
  const cssFilePath = `/theme/${cssFolderName}/variables.css`;

  // Check if an existing theme file is already loaded
  let existingLink = document.getElementById('site-theme');

  if (existingLink) {
    // Replace existing theme
    existingLink.setAttribute('href', cssFilePath);
  } else {
    // Create new theme link
    const link = document.createElement('link');
    link.id = 'site-theme';
    link.rel = 'stylesheet';
    link.href = cssFilePath;
    document.head.appendChild(link);
  }

  console.log(`Loaded theme: ${cssFolderName}`);
};

const updateFavicon = () => {
  let link: any = document.querySelector("link[rel~='icon']");

  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  link.setAttribute('href', getEnvVariable(domain, 'favicon'));
};

const updateTitle = () => {
  document.title = BRAND_NAME;
};

const App: React.FC<StateProps> = (props) => {
  const consoleOpen = useConsoleOpen();
  const helmetContext = {};
  // const consoleOpen = false;

  const {
    logout,
    prefersDark,
    loggedIn,
    setLanguages,
    setLangSelected,
    setLangData,
    langData,
    setAlertMsg,
    setDomainConfig,
  } = props;

  // TODO: make sport book call from here.
  const lang = getLang(sessionStorage?.getItem('lang'));

  useEffect(() => {
    const selectedTheme = localStorage.getItem('userTheme');
    if (selectedTheme) {
      let a = ['darkgreen', 'purple', 'darkvoilet'];
      a.splice(a.indexOf(prefersDark), 1);
      document.body.classList.remove(...a);
      document.body.classList.toggle(prefersDark, true);
    } else {
      localStorage.setItem('userTheme', 'darkgreen');
      document.body.classList.add('darkgreen');
    }
  }, [prefersDark]);

  const handleOnIdle = () => {
    console.log('user is idle');
    if (loggedIn) {
      logout();
    }
  };

  const handleClose = () => {
    console.log('user open dev tools');
  };

  useIdleTimer({
    timeout: 1000 * 60 * 60 * 2, // 2 hours
    onIdle: handleOnIdle,
    debounce: 500,
  });

  useEffect(() => {
    // TODO: this should only be called once.
    getDomainConfig();
    // Load theme file
    loadTheme();
    // Load favicon
    updateFavicon();
    // Update title
    updateTitle();
  }, []);

  const getDomainConfig = async () => {
    try {
      const response = await SVLS_API.get(`/account/v2/books/${BRAND_DOMAIN}`);
      if (response.status === 200) {
        var languages = response?.data?.languages;
        const dConfig: DomainConfig = {
          demoUser: response.data.demo_user_enabled,
          signup:
            BRAND_DOMAIN == response.data.sportBookUrl
              ? response.data.signup_enabled
              : response.data.alias_signup_enabled,
          whatsapp: response.data.whatsapp_support,
          payments: response.data.payments_enabled,
          bonus: response.data.bonus_enabled,
          affiliate: response.data.affiliate_enabled,
          depositWagering: response.data.deposit_wagering_enabled,
          suppportContacts: response.data.support_contacts,
          apkUrl: response.data.apk_url,
        };
        setLanguages(languages);
        setDomainConfig(dConfig);
      }
    } catch (err) {
      console.log(err);
    } finally {
      afterProcess(languages);
    }
  };

  const afterProcess = (languages: string[]) => {
    var sessionLang = sessionStorage.getItem('lang');
    var lang = sessionLang
      ? getUpdatedSelectedLang(languages, sessionLang)
      : getSelectedLang(languages);

    sessionStorage.setItem('lang', lang);
    setLangSelected(sessionStorage.getItem('lang'));
  };

  useEffect(() => {
    if (lang) {
      getLangData();
    }
  }, [lang]);

  const getLangData = async () => {
    try {
      // TODO: uncomment this, if we don't want to take the values from uploaded json.
      // if (lang === defaultLang) {
      //   setLangData(defaultLangData);
      //   return;
      // }

      var langCode = getLangCode(lang)?.toLowerCase();
      const response = await LANG_API.get(
        `/languages/${langCode}/lang.json.gz`
      );
      const data = await response.data;
      if (data) {
        setLangData(data);
      } else {
        setDefaultLangData();
      }
    } catch (error) {
      console.error('Error getting language data:', error);
      setDefaultLangData();
    }
  };

  const setDefaultLangData = () => {
    sessionStorage.setItem('lang', defaultLang);
    setLangSelected(defaultLang);
    setLangData(defaultLangData);
  };

  return (
    <>
      <HelmetProvider context={helmetContext}>
        {!consoleOpen ? (
          <Suspense fallback={<LoadingPage />}>
            <BrowserRouter>
              <Switch>
                <Route path="/access-redirect" component={AccessRedirect} />
                {/* TODO: this component not used. */}
                <Route path="/terms-and-conditions" component={AcceptTerms} />
                <Route path="/reset-password" component={ResetPassword} />
                <Route path="/login" component={LoginPage} />
                <Route path="/register" component={SignUp} />
                <Route path="/forgot-password" component={ForgotPwdForm} />
                <Route path="/maintenance" component={Maintenance} />
                <Route path="/" component={MainPage} />
              </Switch>
            </BrowserRouter>
          </Suspense>
        ) : null}
      </HelmetProvider>

      <Dialog
        open={consoleOpen}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
        fullWidth={true}
        className="dev-tools-msg-modal"
      >
        <DialogContent className="modal-content-ctn">
          <div className="dev-tools-warning-msg">
            {' ' + langData?.['app_security_txt']}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const useConsoleOpen = () => {
  const [consoleOpen, setConsoleOpen] = useState(false);

  useEffect(() => {
    let checkStatus;

    var element = new Image();
    Object.defineProperty(element, 'id', {
      get: function () {
        checkStatus = true;
        throw new Error('Dev tools checker');
      },
    });
    requestAnimationFrame(function check() {
      setTimeout(() => {
        checkStatus = false;
        // Don't delete this console statements
        // Uncomment fater fixing ios issues
        // if (process.env.REACT_APP_NODE_ENV !== 'development') {
        //   console.dir(element);
        //   console.clear();
        // }
        setConsoleOpen(checkStatus);
        requestAnimationFrame(check);
      }, 1000);
    });
  });

  return consoleOpen;
};

const mapStateToProps = (state: RootState) => {
  return {
    mailVerified: state.auth.mailVerified,
    prefersDark: state.common.prefersDark,
    loggedIn: state.auth.loggedIn,
    langData: state.common.langData,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    fetchMailVerifiedSuccess: (mailVerified: boolean) =>
      dispatch(fetchMailVerifiedSuccess(mailVerified)),
    logout: () => dispatch(logout()),
    setLanguages: (languages: string[]) => dispatch(setLanguages(languages)),
    setLangSelected: (lang: string) => dispatch(setLangSelected(lang)),
    setLangData: (langData: any) => dispatch(setLangData(langData)),
    setAlertMsg: (alert: AlertDTO) => dispatch(setAlertMsg(alert)),
    setDomainConfig: (config: DomainConfig) =>
      dispatch(setDomainConfig(config)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
